<script lang="ts" setup>
  import type { FieldMeta } from 'vee-validate';
  import {
    computed,
    ref,
    useAttrs,
    useSlots,
    useTemplateRef,
    watchEffect,
  } from 'vue';
  import { VueTelInput } from 'vue-tel-input';
  import type { CountryObject } from 'vue-tel-input';
  import 'vue-tel-input/vue-tel-input.css';
  import { InputWidthVariant } from '../InputWrapper';
  import { rightSlotClass } from '../InputWrapper/InputWrapper.css.ts';
  import InputWrapper from '../InputWrapper/InputWrapper.vue';
  import {
    phoneInputClass,
    phoneInputLoaderClass,
    phoneInputWrapperClass,
  } from './PhoneInput.css.ts';
  import type { FixedVueTelInputResponse } from './PhoneInput.types.ts';

  export type PhoneInputProps = {
    id?: string;
    testId?: string;
    title?: string;
    modelValue?: string;
    label?: string;
    name: string;
    placeholder?: string;
    error?: string;
    disabled?: boolean;
    readonly?: boolean;
    autofocus?: boolean;
    required?: boolean;
    disableAutoFormat?: boolean;
    allowAnyCharacters?: boolean;
    loading?: boolean;
    meta?: FieldMeta<string>;
    optionalLabel?: boolean;
    width?: InputWidthVariant;
    defaultCountry?: string;
    inputType?: string;
    mode?: 'international' | 'national' | 'auto';
  };

  const props = withDefaults(defineProps<PhoneInputProps>(), {
    placeholder: '',
    disableAutoFormat: false,
    allowAnyCharacters: false,
    optionalLabel: false,
    disabled: false,
    meta: undefined,
    inputType: 'tel',
    width: InputWidthVariant.FULL,
    defaultCountry: 'it',
    mode: 'national',
  });

  const emit = defineEmits<{
    'update:modelValue': [value: string, phoneObj: FixedVueTelInputResponse];
    validate: [phoneObj: FixedVueTelInputResponse];
    'country-changed': [countryObject: CountryObject];
    blur: [ev?: Event];
    focus: [ev?: Event];
    space: [ev?: Event];
    enter: [ev?: Event];
    open: [ev?: Event];
    close: [ev?: Event];
  }>();

  defineOptions({
    inheritAttrs: false,
  });
  const attrs = useAttrs();

  const slots = useSlots();

  const inputOptions = computed(() => ({
    autocomplete: 'on',
    autofocus: props.autofocus,
    id: 'phone-input',
    name: props.name,
    showDialCode: false,
    placeholder: props.placeholder,
    required: props.required,
    readonly: false,
    type: props.inputType,
    styleClasses: [
      phoneInputClass,
      props.loading ? phoneInputLoaderClass : '',
    ].filter(Boolean),
  }));

  const dropdownOptions = computed(() => ({
    disabled: props.disabled || props.loading || props.readonly,
    showDialCodeInList: true,
    showDialCodeInSelection: true,
    showFlags: true,
    showSearchBox: true, // not working in v6.0.1
  }));

  const internalModel = ref<string | undefined>(props.modelValue);

  const onInput = (number: string, phoneObj: FixedVueTelInputResponse) => {
    internalModel.value = number;

    // this check has been added because the phoneObj.number is not always present and it does not trigger the update:modelValue event and the form is not dirty
    if (phoneObj.number || phoneObj.formatted) {
      emit(
        'update:modelValue',
        phoneObj.number || phoneObj.formatted || '',
        phoneObj,
      );
    }
  };

  const onValidate = (phoneObj: FixedVueTelInputResponse) => {
    emit('validate', phoneObj);
  };
  const onCountryChanged = (countryObject: CountryObject) => {
    emit('country-changed', countryObject);
  };

  const input = useTemplateRef<HTMLInputElement>('input');

  watchEffect(() => {
    if (props.autofocus && input.value) {
      input.value.focus();
    }
  });
</script>

<template>
  <InputWrapper
    :id="id"
    :error="error"
    :label="label"
    :meta="meta"
    :width="width"
    :optional-label="optionalLabel"
  >
    <template #inputLabel> <slot name="inputLabel"></slot> </template>
    <template #inputError> <slot name="inputError"></slot> </template>
    <template #default="slotProps">
      <VueTelInput
        id="phone-input"
        ref="input"
        :style-classes="[attrs.class as string, phoneInputWrapperClass]"
        :auto-format="!disableAutoFormat"
        :valid-characters-only="!allowAnyCharacters"
        :default-country="defaultCountry"
        :disabled="disabled || loading || readonly"
        :input-options="inputOptions"
        :aria-readonly="readonly"
        :dropdown-options="dropdownOptions"
        :autofocus="autofocus"
        :model-value="internalModel"
        :mode="mode"
        @on-input="onInput"
        @validate="onValidate"
        @countryChanged="onCountryChanged"
        @blur="emit('blur')"
        @focus="emit('focus')"
        @space="emit('space')"
        @enter="emit('enter')"
        @open="emit('open')"
        @close="emit('close')"
        v-bind="slotProps"
      >
        <template #icon-right>
          <div :class="rightSlotClass">
            <slot v-if="slots.iconRight" name="iconRight"></slot>
          </div>
        </template>
      </VueTelInput>
    </template>
  </InputWrapper>
</template>
